import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { MobileNav } from '@/shared/components/Layout/MobileNav/MobileNav';
import { Sidebar } from '@/shared/components/Layout/Sidebar/Sidebar';
import { Topbar } from '@/shared/components/Layout/Topbar/Topbar';
import { PAGE_SCROLLER_CLASSES } from '@/shared/components/ui/scroll/scroll.tokens';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle/useDocumentTitle';
import { cn } from '@/shared/utils/cn/cn.util';

import { useAppShellLogic } from './useAppShellLogic';

/**
 * The ONE protected layout route (W1): Sidebar + Topbar + content. No page
 * invents its own chrome.
 *
 * `min-h-0` on the scrolling column is the load-bearing class: without it a flex
 * child refuses to shrink below its content and the whole document scrolls
 * instead of the content area, taking the sidebar and topbar out of view.
 *
 * The height is `h-dvh`, not `h-screen`: `100vh` on a mobile browser measures the
 * viewport with the URL bar *hidden*, so the shell runs taller than what is
 * actually visible and the bottom of every page sits under the browser chrome.
 *
 * Navigation has two hosts and one source (`SidebarNav`): the persistent rail from
 * `lg` up, the `MobileNav` drawer below it. The drawer is mounted unconditionally
 * — Radix renders nothing while it is closed, and the alternative (mounting it
 * behind a width check) would need a media-query hook to decide something CSS
 * already knows.
 *
 * The skip link is first in the DOM so a keyboard operator can jump past a
 * sidebar of a dozen items — visually hidden until focused.
 */
export function AppShell() {
  const { t } = useTranslation('uiKit');
  const { isMobileNavOpen, openMobileNav, setMobileNavOpen, mainId, mobileNavId } =
    useAppShellLogic();
  useDocumentTitle();

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground">
      <a
        href={`#${mainId}`}
        className="sr-only rounded-md bg-primary px-lg py-sm text-primary-foreground focus:not-sr-only focus:absolute focus:top-sm focus:start-sm focus:z-fixed"
      >
        {t('shell.skipToContent')}
      </a>

      <Topbar
        mobileNavId={mobileNavId}
        isMobileNavOpen={isMobileNavOpen}
        onOpenMobileNav={openMobileNav}
      />

      <MobileNav id={mobileNavId} isOpen={isMobileNavOpen} onOpenChange={setMobileNavOpen} />

      <div className="flex min-h-0 flex-1">
        <Sidebar />
        {/* Tighter gutter on a phone: 24px each side of a 375px viewport spends
            13% of the screen on nothing. The scroll behaviour is
            `PAGE_SCROLLER_CLASSES` — see `scroll.tokens.ts` for why this one
            pane does NOT contain its scroll chain. */}
        <main id={mainId} className={cn(PAGE_SCROLLER_CLASSES, 'p-lg lg:p-xl')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
